import React, { useState } from 'react';
import { QuestionModel } from '../../../../models';
import { Edit2, Trash2, Check, X, Plus, Trash } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { preprocessLatex } from '../../../../services/latexUtils';

interface QuestionTileProps {
    question: QuestionModel;
    index: number;
    onEdit?: (updatedQuestion: QuestionModel) => void;
    onDelete?: () => void;
}

const QuestionTile: React.FC<QuestionTileProps> = ({ question, index, onEdit, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editQuestionHtml, setEditQuestionHtml] = useState(question.questionHtml || '');
    const [editMsType, setEditMsType] = useState(question.msType || 'multiple');
    const [editAnswer, setEditAnswer] = useState(String(question.answer || ''));
    const [editExplanationHtml, setEditExplanationHtml] = useState(question.explanationHtml || '');
    const [editOptions, setEditOptions] = useState<string[]>(question.options || []);

    const handleSave = () => {
        if (onEdit) {
            onEdit({
                ...question,
                questionHtml: editQuestionHtml,
                msType: editMsType,
                answer: editAnswer,
                explanationHtml: editExplanationHtml,
                options: editOptions,
            });
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditQuestionHtml(question.questionHtml || '');
        setEditMsType(question.msType || 'multiple');
        setEditAnswer(String(question.answer || ''));
        setEditExplanationHtml(question.explanationHtml || '');
        setEditOptions(question.options || []);
        setIsEditing(false);
    };

    const handleOptionChange = (idx: number, val: string) => {
        setEditOptions(prev => {
            const copy = [...prev];
            copy[idx] = val;
            return copy;
        });
    };

    const handleAddOption = () => {
        setEditOptions(prev => [...prev, `선택지 ${prev.length + 1}`]);
    };

    const handleRemoveOption = (idx: number) => {
        setEditOptions(prev => prev.filter((_, i) => i !== idx));
    };

    if (isEditing) {
        return (
            <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3 shadow-sm">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-400">문항 Q{index} 편집</span>
                    <div className="flex gap-1.5">
                        <button
                            onClick={handleSave}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                            title="저장"
                        >
                            <Check size={14} />
                        </button>
                        <button
                            onClick={handleCancel}
                            className="p-1 text-slate-400 hover:bg-slate-50 rounded"
                            title="취소"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">질문 내용 (HTML/LaTeX 가능)</label>
                        <textarea
                            value={editQuestionHtml}
                            onChange={(e) => setEditQuestionHtml(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 custom-scrollbar"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">유형</label>
                            <select
                                value={editMsType}
                                onChange={(e) => setEditMsType(e.target.value as any)}
                                className="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                                <option value="multiple">5지선다형 객관식</option>
                                <option value="short">주관식/단답형</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">정답</label>
                            <input
                                type="text"
                                value={editAnswer}
                                onChange={(e) => setEditAnswer(e.target.value)}
                                placeholder="예: 4 (객관식) 또는 정답내용 (주관식)"
                                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                    </div>

                    {editMsType !== 'short' && (
                        <div className="space-y-1.5">
                            <label className="block text-[8px] font-black text-slate-400 uppercase">선택지 목록</label>
                            <div className="space-y-1">
                                {editOptions.map((opt, oidx) => (
                                    <div key={oidx} className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-slate-400 w-4">{oidx + 1}.</span>
                                        <input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => handleOptionChange(oidx, e.target.value)}
                                            className="flex-1 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                        <button
                                            onClick={() => handleRemoveOption(oidx)}
                                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <Trash size={11} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={handleAddOption}
                                className="flex items-center gap-1 text-[9px] font-black text-indigo-600 hover:text-indigo-700"
                            >
                                <Plus size={10} />
                                <span>선택지 추가</span>
                            </button>
                        </div>
                    )}

                    <div>
                        <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">문제 해설 (LaTeX 가능)</label>
                        <textarea
                            value={editExplanationHtml}
                            onChange={(e) => setEditExplanationHtml(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 custom-scrollbar"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/20 space-y-3 relative hover:border-slate-300 transition-colors">
            <div className="flex items-center justify-between">
                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-[9px] font-black select-none">
                    Q{index}. {question.msType === 'short' ? '주관식' : '객관식'} (정답: {question.answer})
                </span>
                <div className="flex gap-1">
                    <button
                        onClick={() => setIsEditing(true)}
                        className="p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded transition-colors"
                        title="수정"
                    >
                        <Edit2 size={11} />
                    </button>
                    {onDelete && (
                        <button
                            onClick={onDelete}
                            className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded transition-colors"
                            title="삭제"
                        >
                            <Trash2 size={11} />
                        </button>
                    )}
                </div>
            </div>

            <div className="text-xs font-semibold text-slate-800 leading-relaxed [&>p]:m-0 [&>p]:inline-block">
                <ReactMarkdown
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeRaw, rehypeKatex]}
                >
                    {preprocessLatex(question.questionHtml || '')}
                </ReactMarkdown>
            </div>
            
            {question.msType !== 'short' && question.options && question.options.length > 0 && (
                <div className="space-y-1 pl-1">
                    {question.options.map((opt, oidx) => {
                        const isCorrect = String(question.answer) === String(oidx + 1);
                        return (
                            <div key={oidx} className={`text-xs font-medium px-2 py-0.5 rounded-md transition-colors flex items-start gap-1 ${
                                isCorrect ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-600'
                            }`}>
                                <span className="shrink-0">{oidx + 1}.</span>
                                <div className="[&>p]:m-0 [&>p]:inline-block">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkMath, remarkGfm]}
                                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                                    >
                                        {preprocessLatex(opt || '')}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {question.explanationHtml && (
                <div className="pt-2 border-t border-slate-200/60 text-[10px] text-slate-500 font-medium">
                    <span className="font-bold text-slate-700 mr-1 float-left shrink-0">[해설]</span>
                    <div className="[&>p]:m-0 [&>p]:inline-block">
                        <ReactMarkdown
                            remarkPlugins={[remarkMath, remarkGfm]}
                            rehypePlugins={[rehypeRaw, rehypeKatex]}
                        >
                            {preprocessLatex(question.explanationHtml || '')}
                        </ReactMarkdown>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionTile;
